import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ToastType } from '@components/Toast';
import {
  LtlQuoteParametersViewModel,
  LtlQuoteResultsViewModel,
} from '@store/orders/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import isEqual from 'lodash/isEqual';

import { CreditFlagTypeOption, CustomerCreditReviewFlag } from '../types';
import {
  customerLTLSettings,
  ltlCustomerSetting,
  setCustomerLTLSettingsLegacy,
} from './store';
import {
  CarrierScac,
  CustomerLookup,
  EvaAccessIdRequirement,
  ILTLCustomerData,
  ILtlCustomerSetting,
  ILtlCustomerSettingPayload,
  ScacAccount,
  ScacAccountCustomer,
  ScacAccountTypes,
} from './types';

const LTL_CUSTOMER_SETTING_FIELDS: (keyof ILtlCustomerSettingPayload)[] = [
  'quoteExpirationDays',
  'enableCustomerPortalAutoDispatch',
  'enableSuggestedClass',
  'enableAgentQuotesInCustomerPortal',
  'enableCustomerSpecificPricing',
  'enableLtlInCustomerPortal',
  'enableMixedCommodities',
  'enablePrePro',
  'emailAgentIfShipmentNotBuilt',
  'emailAgentIfNoQuoteResult',
  'disableBolInCustomerPortal',
  'enableLtlGapLogic',
  'gapCompressionPercentage',
  'insuranceTermsAccepted',
];

const getLtlCustomerSettingChangeSources = (
  payload: ILtlCustomerSettingPayload,
): ChangeTrackingSource[] => {
  const sources = new Set<ChangeTrackingSource>();
  const current = ltlCustomerSetting.data;

  if (
    LTL_CUSTOMER_SETTING_FIELDS.some(
      (field) => !isEqual(payload[field], current[field]),
    )
  ) {
    sources.add(
      CHANGE_TRACKING_SOURCES.customerDetails.ltlCustomerSettingsAndEmailAlerts,
    );
  }

  if (
    !isEqual(
      payload.carrierSelectionStrategyType,
      current.carrierSelectionStrategyType,
    ) ||
    !isEqual(payload.carriers, current.carriers)
  ) {
    sources.add(
      CHANGE_TRACKING_SOURCES.customerDetails.ltlCarrierSelectionCard,
    );
  }

  return [...sources];
};

const getLtlLegacyChangeSources = (
  payload: ILTLCustomerData,
): ChangeTrackingSource[] => {
  const sources = new Set<ChangeTrackingSource>();
  const current = customerLTLSettings.settings;

  if (
    !isEqual(payload.ltlCostPlus, current.ltlCostPlus) ||
    !isEqual(payload.ltlCostPlusOverrides, current.ltlCostPlusOverrides)
  ) {
    sources.add(CHANGE_TRACKING_SOURCES.customerDetails.ltlCostPlusMargins);
  }

  if (!isEqual(payload.ltlBlockedCarriers, current.ltlBlockedCarriers)) {
    sources.add(CHANGE_TRACKING_SOURCES.customerDetails.ltlBlockedCarriers);
  }

  return [...sources];
};

export const fetchLtlCustomerSetting = async (customerId: number) => {
  let response: V4ApiResponse<ILtlCustomerSetting>;
  try {
    response = await apiClient.get(`LtlSettings/customersetting/${customerId}`);
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch LTL customer settings');
    throw new Error(`Failed to fetch LTL customer settings: ${error}`);
  }
  if (!response.isSuccess) {
    handleToast(ToastType.Error, 'Failed to fetch LTL customer settings');
    throw new Error('Failed to fetch LTL customer settings');
  }
  return response.value;
};

export const updateLtlCustomerSetting = async (
  payload: ILtlCustomerSettingPayload,
  changeTrackingSources?: ChangeTrackingSource[],
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.put(
      'LtlSettings/customersetting',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSources?.length
          ? changeTrackingSources
          : getLtlCustomerSettingChangeSources(payload),
      ),
    );
    return response;
  } catch (error) {
    const first =
      Array.isArray(error) && error.length > 0
        ? (error[0] as Record<string, unknown>)
        : null;
    const message =
      first && 'errorMessage' in first && typeof first.errorMessage === 'string'
        ? first.errorMessage
        : 'Failed to save LTL customer settings';
    handleToast(ToastType.Error, message);
    throw new Error(message);
  }
};

export const updateCarrierSelection = async (
  customerId: number,
  carrierSelectionStrategyType: number,
  carrierIds: number[],
  changeTrackingSources?: ChangeTrackingSource[],
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.put(
      'LtlSettings/customersetting/carrierselection',
      { customerId, carrierSelectionStrategyType, carrierIds },
      buildChangeTrackingRequestConfig(
        changeTrackingSources?.length
          ? changeTrackingSources
          : [CHANGE_TRACKING_SOURCES.customerDetails.ltlCarrierSelectionCard],
      ),
    );
    return response;
  } catch (error) {
    const first =
      Array.isArray(error) && error.length > 0
        ? (error[0] as Record<string, unknown>)
        : null;
    const message =
      first && 'errorMessage' in first && typeof first.errorMessage === 'string'
        ? first.errorMessage
        : 'Failed to save carrier selection';
    handleToast(ToastType.Error, message);
    throw new Error(message);
  }
};

export const fetchCustomerLTLData = async (customerId: string) => {
  try {
    const response: V4ApiResponse<ILTLCustomerData> = await apiClient.get(
      `LTLSettings/${customerId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch customer LTL Data');
    throw new Error(`'Failed to fetch customer LTL Data:  ${error}`);
  }
};

const handleError = (error: unknown, toastMessage: string) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
      return 'Unspecified/unknown error';
    });
    setCustomerLTLSettingsLegacy('errorMessage', errorMessages.join(', '));
  }
  handleToast(ToastType.Error, toastMessage);
};

export const updateCustomerLTLData = async (
  payload: ILTLCustomerData,
  changeTrackingSources?: ChangeTrackingSource[],
) => {
  try {
    const response: V4ApiResponse<ILTLCustomerData> = await apiClient.put(
      'LTLSettings',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSources?.length
          ? changeTrackingSources
          : getLtlLegacyChangeSources(payload),
      ),
    );
    handleToast(ToastType.Success, 'LTL Setting saved Successfully');
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error) {
    handleError(error, 'Failed to save customer LTL Data');
    throw new Error('Failed to save customer LTL Data');
  }
};

export const fetchScacAccountTypes = async () => {
  try {
    const response: V4ApiResponse<ScacAccountTypes> = await apiClient.get(
      'ltl/ScacAccountTypes',
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch SCAC Account Types');
    throw new Error(`'Failed to fetch SCAC Account Types');:  ${error}`);
  }
};

export const fetchEvaAccessIdRequirements = async (
  customerId: number,
  carrierScacId: number,
) => {
  try {
    const response: V4ApiResponse<EvaAccessIdRequirement> = await apiClient.get(
      'ltl/EvaAccessIdRequirement',
      {
        params: {
          carrierScacId,
          customerId,
        },
      },
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch EvaAccess Id Requirements');
    throw new Error(`'Failed to fetch EvaAccess Id Requirements');:  ${error}`);
  }
};

export const fetchBillToName = async (
  customerId: number,
  carrierScacId: number,
) => {
  try {
    const response: V4ApiResponse<string> = await apiClient.get(
      'ltl/BillToName',
      {
        params: {
          carrierScacId,
          customerId,
        },
      },
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleToast(ToastType.Error, 'Failed to fetch Bill To Name');
    throw new Error(`'Failed to fetch Bill To Name');:  ${error}`);
  }
};

export const fetchActiveScacAccounts = async (customerId: number) => {
  try {
    const response: V4ApiResponse<ScacAccount> = await apiClient.get(
      `ltl/csp/customer/${customerId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleToast(
      ToastType.Error,
      'Failed to fetch Customer Specific Pricing Data',
    );
    throw new Error(
      `'Failed to fetch Customer Specific Pricing Data');:  ${error}`,
    );
  }
};

export const fetchPotentialScacAccounts = async (customerId: number) => {
  try {
    const response: V4ApiResponse<CarrierScac> = await apiClient.get(
      `ltl/carrierScac?excludeCustomerId=${customerId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleToast(
      ToastType.Error,
      'Failed to fetch Carriers for Customer Specific Pricing',
    );
    throw new Error(
      `'Failed to fetch Carriers for Customer Specific Pricing');:  ${error}`,
    );
  }
};

export const createScacAccount = async (
  payload: Partial<ScacAccount>,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .customerDetails.ltlCspCreateScacAccount,
) => {
  try {
    const response: V4ApiResponse<number> = await apiClient.post(
      'ltl/scacAccount',
      payload,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error) {
    handleError(
      error,
      'Failed to save customer specific pricing. Please reach out to a developer for assistance.',
    );
    throw new Error(
      'Failed to save customer specific pricing. Please reach out to a developer for assistance.',
    );
  }
};

export const updateScacAccount = async (
  payload: ScacAccount,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .customerDetails.ltlCspUpdateScacAccount,
) => {
  try {
    const response: V4ApiResponse<number> = await apiClient.put(
      `ltl/scacAccount/${payload.id}`,
      payload,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error) {
    handleError(
      error,
      'Failed to save customer specific pricing. Please reach out to a developer for assistance.',
    );
    throw new Error(
      'Failed to save customer specific pricing. Please reach out to a developer for assistance.',
    );
  }
};

export const deleteScacAccount = async (
  scacAccountId: number,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .customerDetails.ltlCspDeleteScacAccount,
) => {
  try {
    await apiClient.delete(
      `ltl/scacAccount/${scacAccountId}`,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
  } catch (error) {
    handleError(
      error,
      'Failed to delete customer specific pricing. Please reach out to a developer for assistance.',
    );
    throw new Error(
      'Failed to delete customer specific pricing. Please reach out to a developer for assistance.',
    );
  }
};

export const fetchPotentialAlternateCustomerOptions = async (
  customerId: number,
) => {
  try {
    const response: V4ApiResponse<CustomerLookup> = await apiClient.get(
      `ltl/csp/enabledCustomers?excludecustomerId=${customerId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleToast(
      ToastType.Error,
      'Failed to fetch Customer Options for sharing Customer Specific Pricing',
    );
    throw new Error(
      `'Failed to fetch Customer Options for sharing Customer Specific Pricing');:  ${error}`,
    );
  }
};

export const fetchAlternateCustomers = async (customerId: number) => {
  try {
    const response: V4ApiResponse<ScacAccountCustomer[]> = await apiClient.get(
      `ltl/ScacAccountAlternateCustomer/${customerId}`,
    );
    if (response.isSuccess) {
      return response.value[0];
    }
  } catch (error: unknown) {
    handleToast(
      ToastType.Error,
      'Failed to fetch Alternate Customers who share CSP',
    );
    throw new Error(
      `'Failed to fetch Alternate Customers who share CSP');:  ${error}`,
    );
  }
};

export const createAlternateCustomer = async (
  customerId: number,
  alternateCustomerId: number,
) => {
  try {
    const response: V4ApiResponse<number> = await apiClient.post(
      'ltl/ScacAccountAlternateCustomer',
      {
        CustomerId: customerId,
        AlternateCustomers: [
          {
            CustomerId: alternateCustomerId,
          },
        ],
      },
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.ltlCspAddAlternateCustomer,
      ]),
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error) {
    handleError(
      error,
      'Failed to save alternate customer. Please reach out to a developer for assistance.',
    );
    throw new Error(
      'Failed to save alternate customer. Please reach out to a developer for assistance.',
    );
  }
};

export const deleteAlternateCustomer = async (alternateCustomerId: number) => {
  try {
    await apiClient.delete(
      `ltl/ScacAccountAlternateCustomer/${alternateCustomerId}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.ltlCspRemoveAlternateCustomer,
      ]),
    );
  } catch (error) {
    handleError(
      error,
      'Failed to remove alternate customer. Please reach out to a developer for assistance.',
    );
    throw new Error(
      'Failed to remove alternate customer. Please reach out to a developer for assistance.',
    );
  }
};

export const createCustomerCreditFlag = async (
  payload: CustomerCreditReviewFlag,
) => {
  try {
    const res: V4ApiResponse<CustomerCreditReviewFlag[]> = await apiClient.post(
      '/creditflag/creditflagcreate',
      [payload],
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.headerFlagCreate,
      ]),
    );
    if (res.isSuccess) {
      return res;
    }
  } catch (error) {
    const e = createError(error, 'Failed to create customer credit flag');
    handleToast(ToastType.Error, e.message);
  }
};

export const updateCustomerCreditFlag = async (
  payload: CustomerCreditReviewFlag,
) => {
  try {
    const res: V4ApiResponse<CustomerCreditReviewFlag> = await apiClient.put(
      '/creditflag',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.headerFlagUpdate,
      ]),
    );
    if (res.isSuccess) {
      return res;
    }
  } catch (error) {
    const e = createError(error, 'Failed to update customer credit flag');
    handleToast(ToastType.Error, e.message);
  }
};

export const getCustomerCreditFlags = async (
  customerId: number,
  showCleared: boolean,
) => {
  try {
    const response: V4ApiResponse<CustomerCreditReviewFlag[]> =
      await apiClient.get(`/CreditFlag/${customerId}/${showCleared}`);
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get customer credit flags');
    handleToast(ToastType.Error, e.message);
  }
};

export const getCreditFlagTypes = async () => {
  try {
    const response: V4ApiResponse<CreditFlagTypeOption[]> =
      await apiClient.get('/CreditFlag/Types');
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to get credit flag types');
    handleToast(ToastType.Error, e.message);
  }
};

export const deleteCustomerCreditFlag = async (id: number) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.delete(
      `/creditflag/${id}`,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.customerDetails.headerFlagDelete,
      ]),
    );
    return response.isSuccess;
  } catch (error) {
    const e = createError(error, 'Failed to delete customer credit flag');
    handleToast(ToastType.Error, e.message);
  }
};

export const getTestLane = async (
  scacAccount: ScacAccount,
  quoteParameters: LtlQuoteParametersViewModel,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .customerDetails.ltlTestLane,
) => {
  try {
    const response: V4ApiResponse<LtlQuoteResultsViewModel> =
      await apiClient.put(
        '/ltl/Rate/TestLane',
        {
          scacAccount,
          quoteParameters,
        },
        buildChangeTrackingRequestConfig([changeTrackingSource]),
      );
    return response;
  } catch (error) {
    const e = createError(error, 'Failed to test lane');
    handleToast(ToastType.Error, e.message);
  }
};
